import {Component, OnDestroy, OnInit} from '@angular/core';
import {NewsletterService} from './services/newsletter.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { finalize, takeUntil} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {SEOService} from '../../shared/services/seo.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalGenericInfoComponent} from '../../shared/components/modal-generic-info/modal-generic-info.component';
import {ProjectService} from '../../shared/services/project.service';
import {IRemoteProfile} from '../../shared/interfaces/iremote-profile';
import {TemplateTypes} from '../../shared/models/template-types.enum';
import {StoreLocatorPageService} from '../store-locator-page/store-locator-page.service';
import {Agenzia} from '../store-locator-page/interfaces/agenzia.interface';
import {ModalSelectAgencyComponent} from '../../shared/components/modal-select-agency/modal-select-agency.component';
import {CmsService} from '../../cms/services/cms.service';
import { NgForm } from '@angular/forms';
import { UrlSites } from 'src/app/enums/url-sites.enum';

@Component({
  selector: 'app-newsletter-page',
  templateUrl: './newsletter-page.component.html',
  styleUrls: ['./newsletter-page.component.scss']
})
export class NewsletterPageComponent implements OnInit, OnDestroy {

  constructor(
    private _modalService: NgbModal,
    private _projectService: ProjectService,
    private _newsletterService: NewsletterService,
    private _seoService: SEOService,
    private _route: ActivatedRoute,
    private _storeLocatorService: StoreLocatorPageService,
    private _cmsService: CmsService
  ) { }

  name: string;
  surname: string;
  email: string;

  loadingStatus: boolean;
  loadingMessage: string;
  message: string;
  messageClass: string;

  brand: any;
  selectedStore: Agenzia;
  hasMultipleStores: boolean = false;
  isIstituzionale: boolean = false;

  profile$: Observable<IRemoteProfile>;

  private storeList: Agenzia[] = [];

  private geoSubscription: Subscription;

  private unsubscription$ = new Subject();

  ngOnInit(): void {
    this._seoService.setTitle('Newsletter');
    this._storeLocatorService.getStores();
    this._storeLocatorService.selectedStore$.pipe(takeUntil(this.unsubscription$)).subscribe((selectedStore) => {
      this.selectedStore = selectedStore;
    });

    this._storeLocatorService.storesList$.pipe(takeUntil(this.unsubscription$)).subscribe((storeList) => {
      if (storeList?.length) {
        // recupero tutti gli stores disponibili
        this.storeList = [...storeList];

        // booleano per verificare se è una multilocata
        this.hasMultipleStores = this.storeList?.length > 1;

        // se non è una multilocata, si seleziona automaticamente l'unico store esistente
        if (!this.hasMultipleStores) {
          this._storeLocatorService.selectedStore$.next(JSON.parse(JSON.stringify(this.storeList[0])));
        }
      }
    });

    this._cmsService.brandConfig$.pipe(takeUntil(this.unsubscription$)).subscribe(brand => {
      if (brand) this.brand = brand;
    });

    this._cmsService.siteUrl$.pipe(takeUntil(this.unsubscription$)).subscribe((siteUrl) => {
      if (siteUrl) {
        if(siteUrl.includes(UrlSites.WTSTORE) || siteUrl.includes(UrlSites.WELCOMETRAVELSHOP) || siteUrl.includes(UrlSites.VACANZEWELCOMETRAVEL) || siteUrl.includes(UrlSites.GEOTN)) {
          this.isIstituzionale = true;
        }
      }
    })

    this.profile$ = this._projectService.getProfile();
    this.loadingMessage = 'Invio richiesta in corso...';
    this._route.queryParamMap.subscribe(e => {
      this.email = e.get('email');
    });
  }

  ngOnDestroy(): void {
    this.geoSubscription?.unsubscribe();

    this.unsubscription$.next(null);
    this.unsubscription$.complete();
  }


  resetForm(form: NgForm): void {
    this.name = undefined;
    this.surname = undefined;
    this.email = undefined;
    form.reset();
  }

  submitRequest(form: NgForm): void {
    if ((!this.email || (this.email && this.email === 'nessuna')) && this.selectedStore.externalAgencyId) {
      // se mancano le email di contatto per l'iscrizione alla newsletter, avviso l'utente con un messaggio di errore
      this.message = 'Si è verificato un errore. Non è possibile iscriversi alla newsletter in questo momento. Contattaci telefonicamente.';
      this.messageClass = 'alert-danger';
      console.info('Errore iscrizione newsletter: email utente e/o id agenzia mancante');
      return;
    }
    this.loadingStatus = true;
    this.geoSubscription = this._newsletterService.subscribeNewsletterOTO({
      name: this.name,
      surname: this.surname,
      email: this.email,
      externalAgencyId: this.selectedStore.externalAgencyId,
      selectedAgencyApikey: this.selectedStore.apiKey
    }
  ).pipe(
      finalize(() => this.loadingStatus = false)
    ).subscribe(e => {
      if (!e.error) {
        this.message = 'Richiesta inviata con successo!';
        this.messageClass = 'alert-success';
        this.resetForm(form);
      } else {
        if (e.responseCode === 409) {
          this.message = e.result;
        } else {
          this.message = 'Si e\' verificato un\'errore';
        }
        this.messageClass = 'alert-danger';
      }
    });
  }

  openPrivacy(): void {
    const templateToLoad = this.isIstituzionale ? TemplateTypes.WTISTITUZIONALE_NEWSLETTER : TemplateTypes.GEO_PRIVACY_NEWSLETTER;
    const title = 'Informativa privacy newsletter';
    if (this.selectedStore) {
      this.openModalPrivacy(title, templateToLoad, this.selectedStore.apiKey);
    } else {
      this.openModalPrivacy(title, templateToLoad);
    }
  }

  openStoreLocatorModal(): void {
    const storeLocatorModalRef = this._modalService.open(
        ModalSelectAgencyComponent,
        {
          ariaLabelledBy: 'modal-store-locator',
          size: 'lg',
          backdrop: (this.selectedStore) ? true : 'static',
          keyboard: false
        }
    );
    storeLocatorModalRef.componentInstance.storeList = this.storeList;
    storeLocatorModalRef.componentInstance.previouslySelectedStore = this.selectedStore;
    storeLocatorModalRef.componentInstance.storeSelected.subscribe(
        (modalResult) => {
          this._storeLocatorService.selectedStore$.next(modalResult);
        }
    );
  }

  private openModalPrivacy(title: string, templateToLoad: string, apikey?: string) {
    const modalRef = this._modalService.open(ModalGenericInfoComponent, {
      ariaLabelledBy: 'modal-basic-title',
      size: 'lg'
    });
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.template = templateToLoad;
    if (apikey) {
      modalRef.componentInstance.selectedAgencyApikey = apikey;
    }
  }
}
